import { HygienaAuthError } from "./utils.js";
import {
  UnifiedLoginAuth,
  UnifiedLoginAuthError,
} from "./unified-login-auth.js";

/**
 * @typedef {object} HygienaConfiguration
 * @property {string} appName
 * @property {string} baseUrl
 * @property {string} [storageKey]
 * @property {"memory" | "localStorage" | "sessionStorage"} [storageType]
 */

/** @typedef {Awaited<ReturnType<UnifiedLoginAuth["login"]>>} UnifiedLoginStateUpdate */
/** @typedef {Required<UnifiedLoginStateUpdate>["data"]} HygienaAuthResponse */
/** @typedef {Required<UnifiedLoginStateUpdate>["account"]} HygienaAccount */
/** @typedef {Required<UnifiedLoginStateUpdate>["token"]} HygienaToken */
/** @typedef {Required<UnifiedLoginStateUpdate>["trigger"]} TriggerType */

/** @typedef {{ busy: boolean, trigger: TriggerType }} HygienaBusyStateChange */

/**
 * @typedef {Omit<CustomEvent, "type" | "detail"> & (
 *   | { type: "auth-login-success", detail: UnifiedLoginStateUpdate }
 *   | { type: "auth-token-refresh-success", detail: UnifiedLoginStateUpdate }
 *   | { type: "auth-token-refresh-failed", detail: HygienaAuthError }
 *   | { type: "auth-reset-password-success", detail: HygienaAccount }
 *   | { type: "auth-reset-password-failed", detail: Error }
 *   | { type: "auth-logout", detail: UnifiedLoginStateUpdate }
 *   | { type: "auth-busy-state-change", detail: HygienaBusyStateChange }
 * )} HygienaEventMessage
 */

/** @typedef {HygienaEventMessage["type"]} HygienaEventType */

/** @typedef {(message: HygienaEventMessage) => void} HygienaEventCallback */

/**
 * @typedef {object} IHygienaClient
 * @property {HygienaAccount | undefined} activeAccount
 * @property {HygienaToken | undefined} activeToken
 * @property {boolean} busy
 * @property {(callback: HygienaEventCallback) => void} addEventCallback
 * @property {(account: HygienaAccount) => void} setActiveAccount
 * @property {() => HygienaAccount[]} getAllAccounts
 * @property {(event: CustomEvent<SubmitEvent>) => Promise<UnifiedLoginStateUpdate | undefined>} login
 * @property {() => Promise<void>} logout
 * @property {() => Promise<HygienaToken | undefined>} acquireTokenSilent
 * @property {(accessToken: string) => Promise<HygienaToken | undefined>} setAccessToken
 * @property {() => Promise<void>} clearCache
 */

// TODO (JM): Add test cases for Angular, React and Node
/** @implements {IHygienaClient} */
export class HygienaClient {
  /** @type {HygienaConfiguration} */
  #config;
  /** @type {UnifiedLoginAuth} */
  #unifiedLoginAuth;
  /** @type {HygienaAccount | undefined} */
  #activeAccount;
  /** @type {HygienaToken | undefined} */
  #activeToken;
  /** @type {Set<HygienaEventCallback>} */
  #eventCallbacks = new Set();
  #busy = false;

  /** @param {HygienaConfiguration} config */
  constructor(config) {
    this.#config = {
      storageType: "memory",
      storageKey: "hygiena-auth-token",
      ...config,
    };
    this.#unifiedLoginAuth = new UnifiedLoginAuth({
      appName: this.#config.appName,
      baseUrl: this.#config.baseUrl,
      storageType: this.#config.storageType,
      storageKey: this.#config.storageKey,
    });

    this.#activeAccount = this.#unifiedLoginAuth.state.account ?? undefined;
    this.#activeToken = this.#unifiedLoginAuth.state.token ?? undefined;

    this.#unifiedLoginAuth.on("token-refresh-success", (event) => {
      const detail = /** @type {CustomEvent<UnifiedLoginStateUpdate>} */ (event)
        .detail;
      this.#handleAuthSuccess(detail);
    });
    this.#unifiedLoginAuth.on("token-refresh-failed", (cause) =>
      this.#handleAuthFailed(
        "token-refresh",
        new HygienaAuthError(
          cause instanceof Error ? cause.message : "Token refresh failed.",
          { cause },
        ),
      ),
    );
  }

  /** @returns {HygienaAccount | undefined} */
  get activeAccount() {
    return this.#activeAccount;
  }

  /** @returns {HygienaToken | undefined} */
  get activeToken() {
    return this.#activeToken;
  }

  /** @returns {boolean} */
  get busy() {
    return this.#busy;
  }

  /** @param {HygienaEventCallback} callback */
  addEventCallback(callback) {
    this.#eventCallbacks.add(callback);
  }

  /** @param {HygienaAccount} account */
  setActiveAccount(account) {
    this.#activeAccount = account;
  }

  /** @param {string} accessToken */
  /** @returns {Promise<HygienaToken | undefined>} */
  async setAccessToken(accessToken) {
    try {
      this.#handleBusyState({ busy: true, trigger: "token-refresh" });
      const result = await this.#unifiedLoginAuth.setSession(accessToken);
      this.#activeAccount = result.account;
      this.#activeToken = result.token;
      return this.#activeToken;
    } catch (cause) {
      const message =
        cause instanceof UnifiedLoginAuthError
          ? cause.message
          : "Could not reach Unified Login to refresh the session.";

      const error = new HygienaAuthError(message, { cause });
      this.#handleAuthFailed("token-refresh", error);

      throw error;
    } finally {
      this.#handleBusyState({ busy: false, trigger: "token-refresh" });
    }
  }

  /** @returns {HygienaAccount[]} */
  getAllAccounts() {
    return this.#activeAccount ? [this.#activeAccount] : [];
  }

  /** @returns {HygienaAccount | undefined} */
  getActiveAccount() {
    return this.#activeAccount;
  }

  /** @returns {HygienaToken | undefined} */
  getActiveToken() {
    return this.#activeToken;
  }

  /**
   * @param {{ username: string, password: string }} payload
   * @returns {Promise<UnifiedLoginStateUpdate | undefined>}
   */
  async login(payload) {
    try {
      this.#handleBusyState({ busy: true, trigger: "login" });
      const loginResult = await this.#unifiedLoginAuth.login(payload);

      this.#handleAuthSuccess(loginResult);

      return loginResult;
    } catch (cause) {
      const fallbackMessage = "Login failed.";
      const message = cause instanceof Error ? cause.message : fallbackMessage;
      const error = new HygienaAuthError(message, { cause });
      this.#handleAuthFailed("login", error);
      throw error;
    } finally {
      this.#handleBusyState({ busy: false, trigger: "login" });
    }
  }

  /** @returns {Promise<void>} */
  async logout() {
    this.#handleBusyState({ busy: true, trigger: "logout" });
    try {
      const detail = await this.#unifiedLoginAuth.logout();
      this.#handleLogout(detail);
    } finally {
      this.#handleBusyState({ busy: false, trigger: "logout" });
    }
  }

  /**
   * Resets the user's password using the provided password-reset token
   * @param {{ token: string, newPassword: string }} payload
   * @returns {Promise<HygienaAuthResponse>} */
  async resetPassword(payload) {
    try {
      this.#handleBusyState({ busy: true, trigger: "password-reset" });
      const result = await this.#unifiedLoginAuth.resetPassword(payload);
      this.#activeAccount = result.account;
      this.#activeToken = result.token;
      return result.data;
    } catch (cause) {
      const message =
        cause instanceof UnifiedLoginAuthError
          ? cause.message
          : "Could not reach Unified Login to reset the password.";

      const error = new HygienaAuthError(message, { cause });
      this.#handleAuthFailed("password-reset", error);

      throw error;
    } finally {
      this.#handleBusyState({ busy: false, trigger: "password-reset" });
    }
  }

  /** @returns {Promise<HygienaToken | undefined>} */
  async acquireTokenSilent() {
    try {
      this.#handleBusyState({ busy: true, trigger: "token-refresh" });
      const result = await this.#unifiedLoginAuth.getSession();
      this.#activeAccount = result.account;
      this.#activeToken = result.token;
      return this.#activeToken;
    } catch (cause) {
      const message =
        cause instanceof UnifiedLoginAuthError
          ? cause.message
          : "Could not reach Unified Login to refresh the session.";

      const error = new HygienaAuthError(message, { cause });
      this.#handleAuthFailed("token-refresh", error);

      throw error;
    } finally {
      this.#handleBusyState({ busy: false, trigger: "token-refresh" });
    }
  }

  /** @returns {Promise<void>} */
  async clearCache() {
    await this.logout();
  }

  /** @param {HygienaBusyStateChange} detail */
  #handleBusyState(detail) {
    this.#busy = detail.busy;

    const customEvent = /** @type {HygienaEventMessage} */ (
      new CustomEvent("auth-busy-state-change", {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    this.broadcastEvent(customEvent);
  }

  /** @param {UnifiedLoginStateUpdate} detail */
  #handleAuthSuccess(detail) {
    this.#activeAccount = detail.account;
    this.#activeToken = detail.token;

    const customEvent = /** @type {HygienaEventMessage} */ (
      new CustomEvent(`auth-${detail.trigger}-success`, {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    this.broadcastEvent(customEvent);
  }

  /**
   * @param {TriggerType} trigger
   * @param {HygienaAuthError} [detail]
   */
  #handleAuthFailed(trigger, detail) {
    this.#activeAccount = undefined;
    this.#activeToken = undefined;
    const cause =
      detail?.cause instanceof UnifiedLoginAuthError ? detail.cause : undefined;
    if (cause?.mustReLogin) {
      this.#unifiedLoginAuth.logout();
    }

    const customEvent = /** @type {HygienaEventMessage} */ (
      new CustomEvent(`auth-${trigger}-failed`, {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    this.broadcastEvent(customEvent);
  }

  /** @param {UnifiedLoginStateUpdate} detail */
  #handleLogout(detail) {
    this.#activeAccount = undefined;
    this.#activeToken = undefined;

    const customEvent = /** @type {HygienaEventMessage} */ (
      new CustomEvent("auth-logout", {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    this.broadcastEvent(customEvent);
  }

  /**
   *  @protected
   *  @param {HygienaEventMessage} message */
  broadcastEvent(message) {
    for (const callback of this.#eventCallbacks) {
      try {
        callback(message);
      } catch (error) {
        console.error("Error in event callback:", error);
      }
    }
  }
}
